import { type ReactEventHandler, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react';

import { usePhoneVerifyDialog } from '../../../hooks/usePhoneVerifyDialog';
import { useStartGame } from '../../../hooks/useStartGame';
import { useVisibility } from '../../../hooks/useVisibility';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import type { Game } from '../../../stores/game/types';
import { useStores } from '../../../stores/useStores';
import { getThumbnailUrl } from '../../../utils/getThumbnailUrl';
import { JackpotBalance } from '../../JackpotBalance/JackpotBalance';
import { PreloadBlur } from '../../PreloadBlur/PreloadBlur';
import { GAME_CARD_ID_PREFIX } from '../constants';
import { useGameLabels } from '../hooks/useGameLabels';
import { TestingBadge } from '../TestingBadge/TestingBadge';

import styles from './GameCardV2.module.scss';

export const GameCardV2 = observer(
  ({
    game,
    rowName,
    gameLabelFF,
    onboarding,
    tooltipRef,
    carousel,
    animated = true,
  }: {
    game: Game;
    rowName: string;
    gameLabelFF: boolean;
    onboarding?: boolean;
    tooltipRef?: null | ((el: HTMLElement | null) => void);
    carousel?: boolean;
    animated?: boolean;
  }) => {
    const lobbyRedesignFlag = useFlag('ui_redesign_lobby', false);
    const { userStore, coinTypeStore } = useStores();
    const { tagGameLabels, iconGameLabels } = useGameLabels(game, gameLabelFF);

    const { moreDetails, masterCasinoGameId, identifier, name: gameName } = game;

    const { coinType } = coinTypeStore;

    const isJackpot = typeof moreDetails === 'object' && 'jackpotFeedGC' in Object(moreDetails);

    const [shouldStickVertically, setShouldStickVertically] = useState(false);

    const { ref, visible } = useVisibility<HTMLAnchorElement>({ defaultState: false, disable: !isJackpot });

    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const { handleStartGame } = useStartGame();
    const { openVerifyPhoneDialog } = usePhoneVerifyDialog();

    const isWaitingForVerification = useRef(false);

    const isPhoneVerificationRequired = useFlag('phone_verification_dialog', true);
    const phoneVerified =
      !isPhoneVerificationRequired || (userStore.profile?.phone && userStore.profile?.phoneVerified);

    const startGame = useCallback(() => {
      if (lobbyRedesignFlag) {
        void analytics.sendEvent('game_launch', {
          event_category: 'lobby_page',
          event_type: 'game_id',
          event_value: `${game.masterCasinoGameId}`,
          event_action: 'click',
        });
      }
      handleStartGame(masterCasinoGameId, rowName);
    }, [lobbyRedesignFlag, handleStartGame, masterCasinoGameId, rowName, game.masterCasinoGameId]);

    const attemptStartingGame = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        if (!phoneVerified && coinType === 'SC') {
          let success = false;
          isWaitingForVerification.current = true;

          const cancelVerification = () => {
            isWaitingForVerification.current = success;
          };

          const onSuccess = () => {
            success = true;
          };

          openVerifyPhoneDialog(onSuccess, {
            beforeClose: cancelVerification,
          });
        } else {
          startGame();
        }
      },
      [openVerifyPhoneDialog, startGame, phoneVerified, coinType],
    );

    useEffect(() => {
      if (isWaitingForVerification.current && phoneVerified) {
        startGame();
      }
    }, [phoneVerified, startGame]);

    const handleImageLoad = useCallback<ReactEventHandler<HTMLImageElement>>((event) => {
      setIsImageLoaded(true);
      const { height, width, parentElement } = event.currentTarget;

      if (!parentElement) {
        return;
      }

      const parentRatio = parentElement.clientHeight / parentElement.clientWidth;
      const ownRatio = height / width;

      setShouldStickVertically(parentRatio > ownRatio);
    }, []);

    const mergedRef = useCallback(
      (element: HTMLAnchorElement | null) => {
        tooltipRef?.(element);
        (ref as any).current = element;
      },
      [ref, tooltipRef],
    );

    const hasTagLabel = !isEmpty(tagGameLabels);
    const hasIconLabel = !isEmpty(iconGameLabels);
    return (
      <a
        ref={mergedRef}
        className={classNames(
          styles.gameCard,
          {
            [styles.onboarding]: onboarding,
            'onboarding-arrow': onboarding,
            [styles.inCarousel]: carousel,
            'animation-popup': animated,
            [styles.withJackpot]: isJackpot,
          },
          'game-card',
        )}
        data-game-id={masterCasinoGameId}
        data-game-name={game.name}
        data-testid={identifier}
        href={`/games/${masterCasinoGameId}`}
        id={GAME_CARD_ID_PREFIX + masterCasinoGameId}
        onClick={attemptStartingGame}
      >
        {isJackpot && visible && (
          <div className={styles.jackpotParent}>
            <JackpotBalance
              badgeUrl={game.masterCasinoProvider?.jackpotBadgeUrl}
              providerId={game.masterCasinoProviderId}
              providerName={game.masterCasinoProvider?.name}
            />
          </div>
        )}
        <PreloadBlur
          className={classNames({ [styles.skeleton]: !isImageLoaded })}
          render={(props) => (
            <div className={styles.container}>
              <img
                {...props}
                alt={gameName}
                className={shouldStickVertically ? styles.thumbnailStickVertically : undefined}
                crossOrigin="anonymous"
                data-testid={identifier}
                loading="lazy"
                src={getThumbnailUrl(game)}
                onLoad={(event) => {
                  handleImageLoad(event);
                  props.onLoad?.();
                }}
              />
              <div className={styles.labelsContainer}>
                {hasTagLabel && (
                  <div className={classNames(styles.labels, { [styles.multiple]: tagGameLabels.length > 1 })}>
                    {tagGameLabels
                      .filter(({ name }) => name)
                      .map(({ id, name, gcStyle, scStyle }) => {
                        const style = coinType === 'GC' ? { ...gcStyle } : { ...scStyle };

                        return (
                          <span
                            key={id}
                            className={classNames(styles.gameLabel, {
                              [styles.multiple]: tagGameLabels.length > 1,
                            })}
                            style={style}
                          >
                            <span className={styles.gameLabelInternal}>{name.toUpperCase()}</span>
                          </span>
                        );
                      })}
                  </div>
                )}
                {hasIconLabel && (
                  <div className={classNames(styles.iconLabels)}>
                    {iconGameLabels.map(({ id, iconSrc }) => (
                      <img
                        key={id}
                        alt="Game Label"
                        className={styles.iconLabel}
                        crossOrigin="anonymous"
                        src={iconSrc}
                      />
                    ))}
                  </div>
                )}
              </div>
              {game.isTestingOnly && <TestingBadge />}
            </div>
          )}
        />
      </a>
    );
  },
);
